/**
 * Kiwiloop Design Tokens — Single Source of Truth
 * Derived from landing/index.html :root variables.
 * All UI in this codebase MUST reference these constants — no hardcoded hex values.
 */
export { designTokens, type DesignTokenName } from './generated/design-tokens'


export const color = {
  // ── Backgrounds ──────────────────────────────────
  dark:        '#0F0F0F',
  darkMid:     '#1C1C1C',
  darkCard:    '#242424',
  darkBorder:  '#2E2E2E',

  // ── Cream / Light surfaces ────────────────────────
  cream:       '#F8F6F1',
  creamDark:   '#EDEAE3',
  white:       '#FFFFFF',

  // ── Text ─────────────────────────────────────────
  ink:         '#1A1A1A',
  inkMid:      '#3D3D3D',
  inkSoft:     '#52525B',
  inkMuted:    '#A1A1AA',

  // ── Brand Primary: Violet ─────────────────────────
  violet:      '#6D28D9',
  violetLight: '#EDE9FE',
  violetDim:   '#C4B5FD',

  // ── Brand Accent: Orange ──────────────────────────
  orange:      '#F97316',
  orangeLight: '#FFF7ED',
  orangeDim:   '#FDBA74',   // amber-300

  // ── Semantic ──────────────────────────────────────
  success:     '#22C55E',   // green-500
  successDim:  '#86EFAC',   // green-300
  error:       '#EF4444',   // red-500
  errorDim:    '#FCA5A5',   // red-300
  warning:     '#F59E0B',   // amber-500
  warningDim:  '#FDE68A',   // amber-200
  blue:        '#3B82F6',   // blue-500
  blueDim:     '#93C5FD',   // blue-300
  /** Projekte-App Glyph (KA-992) — ≠ Dokumente-Orange */
  teal:        '#14B8A6',
  tealDim:     '#5EEAD4',

  // ── Helpers: rgba shorthands ──────────────────────
  violetAlpha:  (a: number) => `rgba(109, 40, 217, ${a})`,
  orangeAlpha:  (a: number) => `rgba(249, 115, 22, ${a})`,
  whiteAlpha:   (a: number) => `rgba(255, 255, 255, ${a})`,
  blackAlpha:   (a: number) => `rgba(0, 0, 0, ${a})`,
  errorAlpha:   (a: number) => `rgba(239, 68, 68, ${a})`,
  successAlpha: (a: number) => `rgba(34, 197, 94, ${a})`,
  warningAlpha: (a: number) => `rgba(245, 158, 11, ${a})`,
  blueAlpha:    (a: number) => `rgba(59, 130, 246, ${a})`,
  tealAlpha:    (a: number) => `rgba(20, 184, 166, ${a})`,
} as const

/**
 * Note card surface — KA-798.
 * PO 2026-09-21: normale Graukarte wie Inbox (`darkCard`), keine Farb-Washes.
 * Farbe bleibt optional als Frontmatter-Metadatum; UI-Karten sind immer grau.
 * PO 2026-09-29 (KA-930): Outline wie Todo-Detail-Chips — leuchtende Hairline
 * statt `darkBorder` (zu nah an `darkCard`, Low-Light unsichtbar).
 */
export const noteSurface = {
  card: color.darkCard,
  border: color.whiteAlpha(0.12),
  swatch: {
    default: color.darkCard,
    yellow: color.warningAlpha(0.45),
    green: color.successAlpha(0.45),
    blue: color.blueAlpha(0.45),
    violet: color.violetAlpha(0.45),
    orange: color.orangeAlpha(0.45),
  },
} as const

export const gradient = {
  brand:  'linear-gradient(135deg, #6D28D9 0%, #F97316 100%)',
  /** Vertikal — Visitenkarten-Akzentstreifen (KA-552) */
  brandVertical: 'linear-gradient(180deg, #6D28D9 0%, #F97316 100%)',
  subtle: 'linear-gradient(180deg, #F8F6F1 0%, #EDEAE3 100%)',
} as const

/**
 * Soft Suggestion Banner — Mail Kontakt/CRM-Prompt u. a. Vorschlagsflächen (KA-552).
 * PO-Wahl 2026-08-11: Variante **soft** (1c), nicht Full-Wash (Kalender) und nicht Neutral.
 * Preview: `docs/design/mail-contact-crm-banner-preview.html`
 */
export const suggestionBanner = {
  soft: {
    /** Leichter Brand-Verlauf → Quiet Surface (kein voller Violett-Wash) */
    surface: `linear-gradient(180deg, ${color.violetAlpha(0.07)} 0%, ${color.whiteAlpha(0.04)} 72%)`,
    border: color.violetAlpha(0.22),
    /** 3px inset Brand-Rail links */
    railWidthPx: 3,
    rail: color.violet,
    iconBg: color.violetAlpha(0.18),
    iconFg: color.violetDim,
    iconBorder: color.violetAlpha(0.35),
    /** Eingebettete Visitenkarte / Content-Card */
    cardBg: color.darkMid,
    cardBorder: color.darkBorder,
    cardShadow: '0 8px 24px rgba(0,0,0,0.28)',
    avatarBg: color.whiteAlpha(0.08),
    avatarFg: color.whiteAlpha(0.72),
    avatarBorder: color.darkBorder,
    cardAccent: gradient.brandVertical,
  },
  /** Vergleich / Kalender-Stil — nicht Default für Kontakt-Banner */
  wash: {
    surface: color.violetAlpha(0.1),
    border: color.violetAlpha(0.35),
    iconBg: color.violetAlpha(0.18),
    iconFg: color.violetDim,
  },
  /** Vergleich — zu flach für Kontakt-Banner (PO) */
  neutral: {
    surface: color.whiteAlpha(0.04),
    border: color.darkBorder,
    iconBg: color.violetAlpha(0.18),
    iconFg: color.violetDim,
    iconBorder: color.violetAlpha(0.35),
  },
} as const

export const font = {
  serif: 'Georgia, "Palatino Linotype", Palatino, serif',
  sans:  '-apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif',
  mono:  'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace',
} as const

export const radius = {
  sm: '4px',
  md: '6px',
  lg: '8px',
  xl: '12px',
  /** App-Icon Squircle (Mobile Homescreen) — KA-321 */
  xxl: '22px',
  pill: '999px',
} as const

/** Feed-spezifische neutrale Tokens — "Quiet Interface" */
export const feed = {
  cardActive:  'rgba(255, 255, 255, 0.07)',
  cardBorder:  'rgba(255, 255, 255, 0.08)',
  badgeBg:          'rgba(255, 255, 255, 0.09)',
  badgeText:        '#A1A1AA',        // = inkMuted
  badgeMarkerBg:    'rgba(109, 40, 217, 0.18)',  // violett — Typen mit Bildmarkierung
  badgeMarkerText:  '#C4B5FD',        // = violetDim
  dividerLine: 'rgba(255, 255, 255, 0.07)',
  dividerText: '#A1A1AA',   // = inkMuted
  inputBg:     'rgba(255, 255, 255, 0.05)',
  inputBorder: 'rgba(255, 255, 255, 0.12)',
} as const

export const layout = {
  hitTarget: '44px',
  todoSideSheet: {
    storageKey: 'kw_todo_sidesheet_width',
    minPx: 440,
    defaultPx: 560,
    maxPx: 720,
    slideInMs: 280,
    slideOutMs: 240,
  },
  /** Messenger Projekt-Rail (KA-1086) — Default ≈ alte Hub-Breite +40 */
  messengerRoomDetail: {
    storageKey: 'kw_messenger_room_detail_width',
    minPx: 360,
    defaultPx: 420,
    maxPx: 640,
  },
  /** Messenger Object-Rail (KA-1123) — PO: min 280 · max 720 · Defaults je Kind */
  messengerObjectRail: {
    storageKey: 'kw_messenger_object_rail_width',
    minPx: 360,
    defaultPx: 420,
    maxPx: 720,
  },
  /**
   * Messenger 1:1 bubbles (KA-1136) — Teams-dark parity.
   * Preview: docs/design/messenger-mobile-shell-preview.html
   */
  messengerChat: {
    chatBg: '#0A0A0A',
    /** Composer-Pill auf chatBg — ohne Outline, vertikal zentriert */
    composerPill: '#161618',
    bubbleIn: '#222226',
    bubbleMine: '#4F2890',
    /** Textauswahl im Thread */
    selection: 'rgba(109, 40, 217, 0.45)',
  },
  /** Personal Mail detail sheet (KA-410) — fixed 720px on desktop (mobile = 100vw). */
  mailSideSheet: {
    storageKey: 'kw_mail_sidesheet_width',
    minPx: 720,
    defaultPx: 720,
    maxPx: 720,
    slideInMs: 280,
    slideOutMs: 240,
  },
  /**
   * ChipPopoverSelect — KA-388 / docs/design/project-chip-select-preview.html
   * Inline project/status/assignee chips: portaled listbox, never native &lt;select&gt;.
   * Flip: if spaceBelow &lt; flipMinBelowPx AND spaceAbove &gt; spaceBelow → open upward.
   */
  chipPopover: {
    /** Preferred menu width floor (px); grows with trigger up to maxWidthPx. */
    minWidthPx: 160,
    maxWidthPx: 280,
    /** Cap for menu height (px); actual maxHeight = min(this, available viewport). */
    maxHeightPx: 280,
    /**
     * Minimum space below trigger (px) to prefer opening downward.
     * Includes search field + clear row + ~4 options (raised from 160 → 220 with search).
     */
    flipMinBelowPx: 220,
    /** Show search field when options.length ≥ this (long project lists). */
    searchThreshold: 8,
    /** Gap between trigger and menu (px). */
    offsetPx: 4,
    /** Viewport edge padding (px). */
    edgePadPx: 8,
  },
  /**
   * Shared mobile app-list chrome — Mail / Docs / Notes / Contacts.
   * KL-APP-LIST-MOBILE-SHELL · KA-876 · Preview: docs/design/app-list-mobile-shell-preview.html
   * Aligns with mailMobile.breakpointMaxPx for when to show the shell.
   */
  appListMobile: {
    /** Same as mailMobile — max-width inclusive → Mobile Shell */
    breakpointMaxPx: 767,
    bodyCardInsetPx: 10,
    chromePadPx: 8,
    zIndex: 40,
    drawerZIndex: 50,
  },
  /**
   * Meine Inbox mobil — KA-648 / KL-MAIL-MOBILE / docs/design/mail-mobile-shell-preview.html
   * PO-Freeze 2026-08-20 · ADR docs/adr-mail-mobile-shell.md
   */
  mailMobile: {
    /** max-width inclusive → Mobile Shell; above → Desktop Side Sheet */
    breakpointMaxPx: 767,
    /** Design-preview reference device */
    deviceRefWidthPx: 393,
    /** PO-Accept 2026-08-20: mobile Touch 40 (nicht Desktop-Parität 32) */
    toolButtonMinHeightPx: 40,
    toolBarGapPx: 4,
    /** Detail body card inset from screen edges */
    bodyCardInsetPx: 10,
    attachChipMinHeightPx: 22,
    attachCardWidthPx: 232,
    /** Gap between attach cards (KA-713 / KL-MAIL-ATTACH-OVERFLOW). */
    attachRowGapPx: 10,
    drawerMaxWidthPx: 320,
    accountRailWidthPx: 56,
    pushSlideMs: 280,
    sheetMaxHeightRatio: 0.72,
  },
  /**
   * Desktop-Listenseite — Mail, Dateien, Notizen.
   * Shared: organisms/AppListFilterBar + molecules/AppListViewTrailing (KA-803).
   * Preview: docs/design/app-list-filter-bar-preview.html
   */
  appListPage: {
    drawerWidthPx: 232,
    columnMaxPx: 960,
    /** Notes masonry + Todos (Kanban) — PO 2026-10-08 · KA-1093 */
    columnMaxWidePx: 1200,
    columnPaddingX: 16,
    columnPaddingTop: 20,
    columnPaddingBottom: 64,
    /** Titel sitzt auf --dark, nicht im Rahmen. */
    titleSizePx: 22,
    titleWeight: 600,
    /** Rahmen der Liste: darkCard + darkBorder + radius.xl */
    frameRadius: '12px',
  },
  /**
   * Ansichts-Chip — AppListViewTrailing (`app-list-view-switch`) · Todos/Mails/Docs/Notes.
   * Shell: `color.darkCard` + `color.darkBorder` + `radius.lg` · Label ▾ · optional ×.
   * ≠ CompoundFilterChip (gestrichelt / violet active) — das ist der ältere Filter-Add.
   * Production: molecules/AppListViewTrailing.tsx · Preview: todos-project-chrome-preview.html
   * Messenger-Rail (KA-1087): gleiche Chip-Anatomie für „Im Chat geteilt“.
   */
  appListViewChip: {
    maxWidthPx: 140,
    maxWidthWithClearPx: 168,
    /** Mit Kicker „Ansicht“ (Messenger-Rail KA-1087 / optional Trailing) */
    maxWidthWithKickerPx: 200,
    maxWidthWithKickerClearPx: 228,
    triggerMaxWidthPx: 140,
    triggerMaxWidthWithClearPx: 128,
    triggerMaxWidthWithKickerClearPx: 188,
    labelMaxWidthPx: 110,
    labelMaxWidthWithClearPx: 96,
    labelMaxWidthWithKickerClearPx: 120,
    minHeightPx: 28,
    paddingYPx: 2,
    paddingXPx: 8,
    gapPx: 4,
    clearWidthPx: 28,
    fontSizePx: 12,
    fontWeight: 600,
    /** Optional prefix — uppercase, inkMuted (Parität TodoViewSwitcher „Ansicht“) */
    kickerFontSizePx: 10,
    kickerFontWeight: 500,
    kickerLetterSpacing: '0.04em',
    caretSizePx: 10,
    clearFontSizePx: 16,
  },
  /**
   * Notizen — Editor-Sheet + Listen-Karte (KA-851 / PO B · Karten-Parität).
   * Preview: docs/design/notes-sheet-inset-decision-preview.html
   * Production: notes/NotesContent.tsx
   */
  notesSheet: {
    /** Head: top · x · bottom — Parität zur Listen-Karte oben */
    headPadTopPx: 14,
    headPadXPx: 16,
    headPadBottomPx: 10,
    /** Body-Scroll: top · x · bottom */
    bodyPadTopPx: 6,
    bodyPadXPx: 16,
    bodyPadBottomPx: 14,
    /** Close im Head — Pill, Icon 18 */
    closeSizePx: 36,
    closeIconPx: 18,
    sheetRadiusPx: 20,
  },
  /** Grid-Karte Content-Pad (ohne Media) */
  notePad: {
    padTopPx: 14,
    padXPx: 16,
    padBottomPx: 14,
  },
  /**
   * Person-Hub / Detail-Sheet Chrome — Stift · Name-on-scroll · X
   * Molecule: molecules/PersonHubChrome.tsx · KA-885
   * Preview: docs/design/person-hub-chrome-preview.html
   * Hub-Previews: contacts-person-hub-card-variants-preview.html
   */
  personHubChrome: {
    padPx: 10,
    iconBtnPx: 36,
    iconStroke: 1.75,
    iconPx: 18,
    titleSizePx: 15,
    titleWeight: 650,
    textBtnMinHeightPx: 36,
    compactRevealMs: 220,
  },
} as const

export const shadow = {
  card:   '0 2px 16px rgba(0,0,0,0.4), 0 0 0 1px rgba(255,255,255,0.05)',
  popover:'0 8px 24px rgba(0,0,0,0.5)',
  button: '0 2px 6px rgba(0,0,0,0.4)',
  /**
   * Lifted reorder ghost (KA-794) — several layers so the card feels “in hand”.
   * Spec: docs/design/LIST-REORDER-MOTION.md
   */
  listReorderLift: '0 8px 24px rgba(0,0,0,0.5), 0 28px 56px rgba(0,0,0,0.35)',
} as const

/**
 * Raised Thumb / sunken Track — Segmented 2+-Option Toggle.
 * PO 2026-10-09 · KA-1132 · Previews: messenger-composer-attach · document-viewer-share-activity
 * Atom: atoms/KwSegmentedToggle.tsx
 */
export const segmentedToggle = {
  trackPaddingPx: 3,
  trackRadiusPx: 10,
  thumbRadiusPx: 8,
  trackBg: color.blackAlpha(0.35),
  trackInsetShadow: `inset 0 1px 3px ${color.blackAlpha(0.45)}`,
  thumbBg: color.darkCard,
  thumbShadow: `0 1px 0 ${color.whiteAlpha(0.06)}, 0 4px 12px ${color.blackAlpha(0.45)}, 0 0 0 1px ${color.darkBorder}`,
  /** Hairline zwischen Optionen (iOS-Segment); aus neben aktivem Thumb */
  divider: color.whiteAlpha(0.12),
  dividerInsetPct: 28,
  /** Single-line labels (z. B. Gerät | Kiwiloop) */
  minHeightPx: 44,
  /** Mit Subtitle (z. B. Document Share Art) */
  minHeightWithSubPx: 40,
  labelSizePx: 13,
  labelSizeCompactPx: 12,
  subSizePx: 10,
  colorMs: 150,
} as const

/**
 * Motion tokens — shared timing / physics for Product UI.
 * List reorder: PO-approved 2026-09-20 („jetzt ist es richtig“, KA-794).
 * Full pattern: docs/design/LIST-REORDER-MOTION.md
 */
export const motion = {
  /** Composer reveal: restrained settling, stationary controls, reduced-motion opt-out. */
  composerToolbar: {
    revealMs: 280,
    effectsMs: 150,
    pressMs: 120,
    ease: 'cubic-bezier(0.22, 1, 0.36, 1)',
    pressScale: 0.96,
    travelPx: 4,
    settlePx: 1,
  },
  listReorder: {
    /** Sibling gap-open / FLIP */
    shiftMs: 280,
    /** Ghost settle into slot */
    settleMs: 280,
    ease: 'cubic-bezier(0.22, 1, 0.36, 1)',
    /** Lifted ghost scale while dragging */
    liftScale: 1.05,
    liftScaleReduced: 1.04,
    /** EMA alpha for pointer velocity → tilt */
    tiltAlpha: 0.18,
    /** Multiply smoothed velocity (px/ms) for degrees */
    tiltGain: 10,
    /** Max ghost tilt — keep low; wide rows amplify rotation at the free end */
    tiltMaxDeg: 2,
    /** Fraction of row height before insert index may change */
    insertHysteresis: 0.28,
  },
} as const

/**
 * Per-author avatar colour palette — deterministically assigned by name hash.
 * Six visually distinct hues, all dark-mode optimised.
 * Use `authorPalette[hash % authorPalette.length]` — see `feed/utils.ts`.
 */
export const authorPalette = [
  { bg: color.violetAlpha(0.15), text: color.violetDim  },  // violet
  { bg: color.orangeAlpha(0.15), text: '#FDBA74'         },  // orange  (amber-300)
  { bg: 'rgba(20,184,166,0.15)', text: '#5EEAD4'         },  // teal    (teal-300)
  { bg: color.successAlpha(0.15),text: color.successDim  },  // green
  { bg: 'rgba(14,165,233,0.15)', text: '#7DD3FC'         },  // sky     (sky-300)
  { bg: 'rgba(6,182,212,0.15)',  text: '#67E8F9'         },  // cyan    (cyan-300)
] as const
